/**
 * Mock data for the Finance → Invoices UI. Stands in for the TMS invoicing API
 * until the backend lands — shapes mirror what the API will return. Numbers are
 * illustrative and internally consistent so the tabs add up.
 */
import type { ApprovalOrder, CurrencyCode, InvoiceSummaryRow } from './types';

export const MOCK_CUSTOMERS = [
  { id: 'bk', name: 'BK Logistics', code: 'BK-001', currency: 'EUR' as CurrencyCode },
  { id: 'spr', name: 'SPR Logistics', code: 'SPR', currency: 'EUR' as CurrencyCode },
  { id: 'gt', name: 'Global Trans', code: 'GT', currency: 'GBP' as CurrencyCode },
];

const BK = { customerId: 'bk', customerName: 'BK Logistics', customerCode: 'BK-001', currency: 'EUR' as CurrencyCode };
const SPR = { customerId: 'spr', customerName: 'SPR Logistics', customerCode: 'SPR', currency: 'EUR' as CurrencyCode };

/** Orders pending approval-for-invoicing (drives the For-approval + Create tabs). */
export const MOCK_APPROVAL_ORDERS: ApprovalOrder[] = [
  {
    ...BK,
    id: 'o-7',
    orderNumber: '2606-000007',
    collectionPoint: 'Ballymount Corporate Park, Dublin',
    deliveryPoint: 'Royal Mail SDO, Manchester',
    collected: '03 Jun',
    delivered: '03 Jun',
    cargo: '8 × Euro Pallet',
    transport: 320,
    fuel: 20.8,
    other: null,
    reference: 'BK-PO-9942',
    status: 'eligible',
    charges: [
      { id: 'c-7-1', kind: 'transport', label: 'Transport', amount: 320, meta: '8 × Euro Pallet · Ballymount → Royal Mail SDO, Manchester' },
      { id: 'c-7-2', kind: 'fuel', label: 'Fuel surcharge', amount: 20.8, tag: 'Rate at 10/06/26 · 6.5%', meta: '6.5% of transport ex-VAT, ex-fuel' },
    ],
  },
  {
    ...BK,
    id: 'o-6',
    orderNumber: '2606-000006',
    collectionPoint: 'Ballymount Corporate Park, Dublin',
    deliveryPoint: '4 W Bank Rd, Belfast',
    collected: '03 Jun',
    delivered: '03 Jun',
    cargo: '10 × Standard Pallet',
    transport: 250,
    fuel: 16.25,
    other: 45,
    reference: 'BK-PO-9941',
    status: 'eligible',
    charges: [
      { id: 'c-6-1', kind: 'transport', label: 'Transport', amount: 250, meta: '10 × Standard Pallet · Ballymount → 4 W Bank Rd, Belfast' },
      { id: 'c-6-2', kind: 'fuel', label: 'Fuel surcharge', amount: 16.25, tag: 'Rate at 10/06/26 · 6.5%', meta: '6.5% of transport ex-VAT, ex-fuel' },
      { id: 'c-6-3', kind: 'other', label: 'Waiting time', amount: 45, tag: 'Manual', meta: 'Added by finance · 2hrs' },
    ],
  },
  {
    ...BK,
    id: 'o-5',
    orderNumber: '2606-000005',
    collectionPoint: 'Ballymount Corporate Park, Dublin',
    deliveryPoint: 'Unnamed Rd, Bootle',
    collected: '28 May',
    delivered: '29 May',
    cargo: '20 × Euro Pallet',
    transport: 480,
    fuel: 31.2,
    other: null,
    status: 'exception',
    exception: 'No POD uploaded',
    charges: [
      { id: 'c-5-1', kind: 'transport', label: 'Transport', amount: 480 },
      { id: 'c-5-2', kind: 'fuel', label: 'Fuel surcharge', amount: 31.2, tag: 'Rate at 10/06/26 · 6.5%' },
    ],
  },
  {
    ...BK,
    id: 'o-4b',
    orderNumber: '2606-000004',
    collectionPoint: '14 Killadoon Pk, Celbridge',
    deliveryPoint: 'Ballymount Corporate Park',
    collected: '01 Jun',
    delivered: '02 Jun',
    cargo: '— (TBC)',
    transport: null,
    fuel: null,
    other: null,
    status: 'exception',
    exception: 'No charges set',
    charges: [],
  },
  {
    ...SPR,
    id: 'o-3',
    orderNumber: '2606-000003',
    collectionPoint: 'Ballymount Corporate Park, Dublin',
    deliveryPoint: 'Kilnamanagh, Dublin',
    collected: '03 Jun',
    delivered: '03 Jun',
    cargo: '10 × Boxes of something',
    transport: 200,
    fuel: 14.4,
    other: null,
    reference: 'SPR-2241',
    status: 'eligible',
    charges: [
      { id: 'c-3-1', kind: 'transport', label: 'Transport', amount: 200, meta: '10 × Boxes · Ballymount → Kilnamanagh, Dublin' },
      { id: 'c-3-2', kind: 'fuel', label: 'Fuel surcharge', amount: 14.4, tag: 'Rate at 10/06/26 · 6.5%' },
    ],
  },
  {
    ...SPR,
    id: 'o-2',
    orderNumber: '2606-000002',
    collectionPoint: 'Ballymount Corporate Park, Dublin',
    deliveryPoint: 'Kilnamanagh, Dublin',
    collected: '03 Jun',
    delivered: '03 Jun',
    cargo: '10 × Boxes of something',
    transport: 180,
    fuel: 12.96,
    other: 30,
    status: 'exception',
    exception: 'No customer ref',
    charges: [
      { id: 'c-2-1', kind: 'transport', label: 'Transport', amount: 180 },
      { id: 'c-2-2', kind: 'fuel', label: 'Fuel surcharge', amount: 12.96, tag: 'Rate at 10/06/26 · 6.5%' },
      { id: 'c-2-3', kind: 'other', label: 'Handling fee', amount: 30, tag: 'Manual' },
    ],
  },
  {
    ...SPR,
    id: 'o-1',
    orderNumber: '2606-000001',
    collectionPoint: 'Ballymount Corporate Park, Dublin',
    deliveryPoint: 'Robinstown, Mullingar',
    collected: '02 Jun',
    delivered: '02 Jun',
    cargo: '6 × Euro Pallet',
    transport: 160,
    fuel: 11.52,
    other: null,
    reference: 'SPR-2238',
    status: 'eligible',
    charges: [
      { id: 'c-1-1', kind: 'transport', label: 'Transport', amount: 160, meta: '6 × Euro Pallet · Ballymount → Robinstown, Mullingar' },
      { id: 'c-1-2', kind: 'fuel', label: 'Fuel surcharge', amount: 11.52, tag: 'Rate at 10/06/26 · 6.5%' },
    ],
  },
];

/** Standard VAT used in the mock builder/summary. */
export const MOCK_VAT_PCT = 23;

/** Generated invoices + credit notes (Invoice-summary tab). */
export const MOCK_INVOICES: InvoiceSummaryRow[] = [
  { id: 'i1', number: 'INV-2026-0142', docType: 'Standard', customerId: 'bk', customerName: 'BK Logistics', customerCode: 'BK-001', currency: 'EUR', orders: '2606-000007, -000006', subtotal: 515.73, vatPct: 23, vat: 118.62, total: 634.35, status: 'Draft', date: '10/06/26' },
  { id: 'i2', number: 'INV-2026-0138', docType: 'Partial', customerId: 'bk', customerName: 'BK Logistics', customerCode: 'BK-001', currency: 'EUR', orders: '2606-000005', subtotal: 192, vatPct: 23, vat: 44.16, total: 236.16, status: 'Sent', date: '02/06/26' },
  { id: 'i3', number: 'INV-2026-0118', docType: 'Adhoc', customerId: 'bk', customerName: 'BK Logistics', customerCode: 'BK-001', currency: 'EUR', orders: '—', subtotal: 150, vatPct: 23, vat: 34.5, total: 184.5, status: 'Paid', date: '01/06/26' },
  { id: 'i4', number: 'INV-2026-0141', docType: 'Standard', customerId: 'spr', customerName: 'SPR Logistics', customerCode: 'SPR', currency: 'EUR', orders: '2606-000004, -000003', subtotal: 437.36, vatPct: 23, vat: 100.59, total: 537.95, status: 'Issued', date: '09/06/26' },
  { id: 'i5', number: 'CN-2026-0012', docType: 'Credit', customerId: 'spr', customerName: 'SPR Logistics', customerCode: 'SPR', currency: 'EUR', orders: '2605-000071', subtotal: 45, vatPct: 23, vat: 10.35, total: 55.35, status: 'Issued', date: '08/06/26' },
  { id: 'i6', number: 'INV-2026-0129', docType: 'Standard', customerId: 'gt', customerName: 'Global Trans', customerCode: 'GT', currency: 'GBP', orders: '2605-000088', subtotal: 820, vatPct: 20, vat: 164, total: 984, status: 'Overdue', date: '27/05/26', overdueDays: 14 },
];

/** Headline numbers for the Invoice-summary stat cards (whole book, not the page). */
export const MOCK_SUMMARY_STATS = {
  draftCount: 4,
  draftAccruing: 3210,
  issuedCount: 18,
  outstanding: 42880,
  overdueCount: 3,
  overdueValue: 7640,
  paidThisMonth: 28450,
  paidThisMonthCount: 11,
};

/** Finance assistant (MyI) panel data. */
export const MOCK_FINANCE_ASSISTANT = {
  overdue: [
    { id: 'od1', customer: 'Global Trans', number: 'INV-0129', days: 14, amount: 984, currency: 'GBP' as CurrencyCode },
    { id: 'od2', customer: 'BK Logistics', number: 'INV-0122', days: 9, amount: 1240, currency: 'EUR' as CurrencyCode },
    { id: 'od3', customer: 'Galway Freight', number: 'INV-0115', days: 6, amount: 820, currency: 'EUR' as CurrencyCode },
  ],
  creditLimit: { customer: 'BK Logistics', outstanding: 4834, limit: 5000 },
  month: { invoiced: 74560, collected: 28450, outstanding: 42880, overdue: 7640 },
};
